/**
 * Projects service layer.
 *
 * THE ONLY FILE that touches `public.projects` for CRUD. Components
 * must import from here — never from src/lib/supabase directly
 * (CLAUDE.md rule 1).
 *
 * Also provides a localStorage-backed "guest" path so unauthenticated
 * users can open the editor and save a project without signing up.
 * On successful sign-in, `migrateGuestProjectsToUser()` bulk-copies
 * local projects into Supabase (A-07).
 *
 * M-04: createProject() enforces the free-plan project limit by calling
 * getUserPlan() before inserting. Cloud projects only — guest (localStorage)
 * projects are never gated.
 */

import { supabase } from "../supabase";
import type { Project } from "../../types";
import type {
  NewProjectInput,
  ProjectError,
  ProjectPatch,
  ProjectResult,
} from "../../types/project";
import { getUserPlan } from "./billing";
import { PLAN_LIMITS } from "../../types/billing";

const GUEST_STORAGE_KEY = "cutmate:guest-projects:v1";
const GUEST_PROJECT_ID_PREFIX = "guest-";

/** Guest project IDs are `guest-<uuid>` and live only in localStorage. */
export function isGuestProjectId(id: string | null | undefined): boolean {
  return typeof id === "string" && id.startsWith(GUEST_PROJECT_ID_PREFIX);
}

// ─── Supabase path ──────────────────────────────────────────────────────────

function mapSupabaseError(error: unknown): ProjectError {
  if (!error || typeof error !== "object") {
    return { code: "unknown", message: "Something went wrong." };
  }
  const err = error as { message?: string; code?: string; status?: number };
  const msg = (err.message ?? "").toLowerCase();
  if (err.code === "PGRST116" || msg.includes("not found")) {
    return { code: "not_found", message: "Project not found." };
  }
  if (err.status === 401 || err.status === 403) {
    return { code: "unauthorized", message: "You're not signed in." };
  }
  if (msg.includes("fetch") || msg.includes("network")) {
    return { code: "network", message: "Network error. Try again." };
  }
  return { code: "unknown", message: err.message ?? "Unknown error." };
}

interface DbRow {
  id: string;
  user_id: string;
  name: string;
  material: string | null;
  units: Project["units"];
  kerf: number;
  edge_padding: number;
  sheets: Project["sheets"];
  parts: Project["parts"];
  layout: Project["layout"] | null;
  created_at: string;
  updated_at: string;
}

function dbRowToProject(row: DbRow): Project {
  return {
    id: row.id,
    userId: row.user_id,
    name: row.name,
    material: row.material ?? undefined,
    units: row.units,
    kerf: Number(row.kerf),
    edgePadding: Number(row.edge_padding),
    sheets: row.sheets ?? [],
    parts: row.parts ?? [],
    layout: row.layout ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function projectToDbInsert(
  userId: string,
  input: NewProjectInput,
): Omit<DbRow, "id" | "created_at" | "updated_at"> {
  return {
    user_id: userId,
    name: input.name,
    material: input.material ?? null,
    units: input.units ?? "metric",
    kerf: input.kerf ?? 3,
    edge_padding: input.edgePadding ?? 10,
    sheets: input.sheets ?? [],
    parts: input.parts ?? [],
    layout: null,
  };
}

function patchToDbUpdate(patch: ProjectPatch): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (patch.name !== undefined) out.name = patch.name;
  if (patch.material !== undefined) out.material = patch.material;
  if (patch.units !== undefined) out.units = patch.units;
  if (patch.kerf !== undefined) out.kerf = patch.kerf;
  if (patch.edgePadding !== undefined) out.edge_padding = patch.edgePadding;
  if (patch.sheets !== undefined) out.sheets = patch.sheets;
  if (patch.parts !== undefined) out.parts = patch.parts;
  if (patch.layout !== undefined) out.layout = patch.layout ?? null;
  return out;
}

async function getUserId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.user.id ?? null;
}

export async function listProjects(): Promise<ProjectResult<Project[]>> {
  const userId = await getUserId();
  if (!userId) return { ok: true, data: listGuestProjects() };

  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .eq("user_id", userId)
    .order("updated_at", { ascending: false });

  if (error) return { ok: false, error: mapSupabaseError(error) };
  return { ok: true, data: (data as DbRow[]).map(dbRowToProject) };
}

export async function getProject(id: string): Promise<ProjectResult<Project>> {
  const userId = await getUserId();
  if (!userId) {
    const guest = getGuestProject(id);
    if (!guest) {
      return {
        ok: false,
        error: { code: "not_found", message: "Project not found." },
      };
    }
    return { ok: true, data: guest };
  }

  const { data, error } = await supabase
    .from("projects")
    .select("*")
    .eq("id", id)
    .single();

  if (error) return { ok: false, error: mapSupabaseError(error) };
  return { ok: true, data: dbRowToProject(data as DbRow) };
}

export async function createProject(
  input: NewProjectInput,
): Promise<ProjectResult<Project>> {
  const userId = await getUserId();
  if (!userId) {
    return { ok: true, data: saveGuestProject(input) };
  }

  // M-04 — enforce per-plan project count limit before inserting.
  // Guest (localStorage) projects are not counted against this limit.
  //
  // PHASE 2 — this gate is client-side only. Two known limitations:
  // 1. A user calling the Supabase REST API directly can bypass it.
  // 2. Two concurrent createProject calls can race past the count check.
  // Real enforcement requires a Postgres trigger or RLS policy. Tracked in issue #149.
  const plan = await getUserPlan();
  const limit = PLAN_LIMITS[plan].maxCloudProjects;

  if (isFinite(limit)) {
    const { count, error: countError } = await supabase
      .from("projects")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId);

    if (countError) {
      return { ok: false, error: mapSupabaseError(countError) };
    }

    if ((count ?? 0) >= limit) {
      return {
        ok: false,
        error: {
          code: "project_limit_reached",
          message: `Free plan supports up to ${limit} saved projects. Upgrade to Pro for unlimited.`,
        },
      };
    }
  }

  const { data, error } = await supabase
    .from("projects")
    .insert(projectToDbInsert(userId, input))
    .select("*")
    .single();

  if (error) return { ok: false, error: mapSupabaseError(error) };
  return { ok: true, data: dbRowToProject(data as DbRow) };
}

export async function updateProject(
  id: string,
  patch: ProjectPatch,
): Promise<ProjectResult<Project>> {
  const userId = await getUserId();
  if (!userId) {
    const updated = updateGuestProject(id, patch);
    if (!updated) {
      return {
        ok: false,
        error: { code: "not_found", message: "Guest project not found." },
      };
    }
    return { ok: true, data: updated };
  }

  const update = patchToDbUpdate(patch);
  const { data, error } = await supabase
    .from("projects")
    .update(update)
    .eq("id", id)
    .select("*")
    .single();

  if (error) return { ok: false, error: mapSupabaseError(error) };
  return { ok: true, data: dbRowToProject(data as DbRow) };
}

export async function deleteProject(id: string): Promise<ProjectResult<null>> {
  const userId = await getUserId();
  if (!userId) {
    deleteGuestProject(id);
    return { ok: true, data: null };
  }

  const { error } = await supabase.from("projects").delete().eq("id", id);
  if (error) return { ok: false, error: mapSupabaseError(error) };
  return { ok: true, data: null };
}

// ─── Guest (localStorage) path ──────────────────────────────────────────────

function readGuestStore(): Project[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(GUEST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Project[]) : [];
  } catch {
    return [];
  }
}

function writeGuestStore(projects: Project[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(projects));
}

export function listGuestProjects(): Project[] {
  return readGuestStore().sort((a, b) =>
    b.updatedAt.localeCompare(a.updatedAt),
  );
}

export function getGuestProject(id: string): Project | null {
  return readGuestStore().find((p) => p.id === id) ?? null;
}

export function saveGuestProject(input: NewProjectInput): Project {
  const now = new Date().toISOString();
  const project: Project = {
    id: `${GUEST_PROJECT_ID_PREFIX}${crypto.randomUUID()}`,
    userId: null,
    name: input.name,
    material: input.material,
    units: input.units ?? "metric",
    kerf: input.kerf ?? 3,
    edgePadding: input.edgePadding ?? 10,
    sheets: input.sheets ?? [],
    parts: input.parts ?? [],
    createdAt: now,
    updatedAt: now,
  };
  writeGuestStore([project, ...readGuestStore()]);
  return project;
}

export function updateGuestProject(
  id: string,
  patch: ProjectPatch,
): Project | null {
  const store = readGuestStore();
  const idx = store.findIndex((p) => p.id === id);
  if (idx < 0) return null;
  const next: Project = {
    ...store[idx]!,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  store[idx] = next;
  writeGuestStore(store);
  return next;
}

export function deleteGuestProject(id: string): void {
  writeGuestStore(readGuestStore().filter((p) => p.id !== id));
}

// ─── Guest → user migration (A-07) ──────────────────────────────────────────

/**
 * Bulk-copy any locally stored guest projects into Supabase under the
 * currently signed-in user. Idempotent: drains localStorage on success.
 */
export async function migrateGuestProjectsToUser(): Promise<
  ProjectResult<{ migrated: number }>
> {
  const userId = await getUserId();
  if (!userId) {
    return {
      ok: false,
      error: { code: "unauthorized", message: "No signed-in user." },
    };
  }

  const guestProjects = readGuestStore();
  if (guestProjects.length === 0) return { ok: true, data: { migrated: 0 } };

  const rows = guestProjects.map((p) =>
    projectToDbInsert(userId, {
      name: p.name,
      material: p.material,
      units: p.units,
      kerf: p.kerf,
      edgePadding: p.edgePadding,
      sheets: p.sheets,
      parts: p.parts,
    }),
  );

  const { error } = await supabase.from("projects").insert(rows);
  if (error) return { ok: false, error: mapSupabaseError(error) };

  writeGuestStore([]);
  return { ok: true, data: { migrated: rows.length } };
}
